import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TICKETS_OPEN_URL } from '../../../../constants';
import { FEATURE_FLAGS_KEYS } from '../../../../constants/featureFlags';
import { useFeatureFlagQuery } from '../../../../remote-state/featureFlagsHooks';
import { selectIsClassicPreloaded, setIsClassicPreloaded } from '../../../../store/globalSlice';
import { FALLBACK_TIMEOUT } from './classic-sr-edit-preload.const';
import { ClassicSREditPreloadProps, TimeoutRef } from './classic-sr-edit-preload.type';

const ClassicSREditPreload: FC<ClassicSREditPreloadProps> = ({ srId }) => {
  const dispatch = useDispatch();
  const { data: isSrPreloadEnabled, isLoading: isFFLoading } = useFeatureFlagQuery({
    flagKey: FEATURE_FLAGS_KEYS.CLASSIC_SR_PRELOAD,
    defaultValue: true,
  });
  const isPreloaded = useSelector(selectIsClassicPreloaded);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const safetyTimeoutRef = useRef<TimeoutRef | null>(null);
  const [classicIframeUrl, setClassicIframeUrl] = useState<string>();

  const isPreloadReady = useMemo(
    () => isSrPreloadEnabled && !isPreloaded && !!classicIframeUrl && !isFFLoading,
    [classicIframeUrl, isFFLoading, isPreloaded, isSrPreloadEnabled],
  );

  const clearClassicIframeTimer = useCallback(() => {
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
    }
  }, []);

  const clearClassicIframeUrl = useCallback(() => {
    setClassicIframeUrl(undefined);
    dispatch(setIsClassicPreloaded(true));
    clearClassicIframeTimer();
  }, [clearClassicIframeTimer, dispatch]);

  const handleIframeLoad = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (iframeWindow) {
      clearClassicIframeUrl();
    }
  }, [clearClassicIframeUrl]);

  const handleIframeError = useCallback(() => {
    clearClassicIframeUrl();
  }, [clearClassicIframeUrl]);

  useEffect(() => {
    if (srId && !isPreloaded) {
      const url = TICKETS_OPEN_URL.CLASSIC(srId);
      setClassicIframeUrl(url);
    }
  }, [srId, isPreloaded]);

  useEffect(() => {
    clearClassicIframeTimer();

    if (classicIframeUrl) {
      safetyTimeoutRef.current = setTimeout(() => {
        clearClassicIframeUrl();
      }, FALLBACK_TIMEOUT);
    }

    return () => {
      clearClassicIframeTimer();
    };
  }, [classicIframeUrl, clearClassicIframeUrl, clearClassicIframeTimer]);

  if (!isPreloadReady) {
    return null;
  }

  return (
    <iframe
      ref={iframeRef}
      src={classicIframeUrl}
      style={{ display: 'none' }}
      onLoad={handleIframeLoad}
      onError={handleIframeError}
      title="Classic View Preload"
    />
  );
};

export default memo(ClassicSREditPreload);
